/**
 * Structured logger for the web package.
 *
 * Provides four log levels with production-safe defaults:
 * - `debug` / `info` — only emit when `import.meta.env.DEV` is true
 * - `warn` / `error` — always emit (production-safe)
 *
 * @example
 *   debug('Cache miss', { key: 'user:123' });
 *   info('User logged in', { userId: 42 });
 *   warn('Rate limit approaching', { attempts: 5 });
 *   error('Failed to fetch orders', { error: err });
 */

type Metadata = Record<string, unknown>;

function withMetadata(
  consoleFn: typeof console.debug | typeof console.info | typeof console.warn | typeof console.error,
  message: string,
  metadata?: Metadata
): void {
  if (metadata !== undefined) {
    consoleFn(message, metadata);
  } else {
    consoleFn(message);
  }
}

export function debug(message: string, metadata?: Metadata): void {
  if (import.meta.env.DEV) {
    withMetadata(console.debug, message, metadata);
  }
}

export function info(message: string, metadata?: Metadata): void {
  if (import.meta.env.DEV) {
    withMetadata(console.info, message, metadata);
  }
}

export function warn(message: string, metadata?: Metadata): void {
  withMetadata(console.warn, message, metadata);
}

export function error(message: string, metadata?: Metadata): void {
  withMetadata(console.error, message, metadata);
}
